import React, { useState, useEffect } from 'react';
import { groupsAPI, usersAPI, clustersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ActionsMenu, { MENU_ITEM } from '../components/ActionsMenu';

const ALL_SECTIONS = [
  'clusters', 'servers', 'workloads', 'networking',
  'storage', 'configuration', 'gpu', 'metrics',
  'alerts', 'playbooks', 'vpn',
];

const SECTION_LABELS = {
  clusters: 'Clusters', servers: 'Servers', workloads: 'Workloads',
  networking: 'Networking', storage: 'Storage', configuration: 'Configuration',
  gpu: 'GPU', metrics: 'Metrics', alerts: 'Alerts', playbooks: 'Playbooks', vpn: 'VPN',
};

const S = {
  page: { padding: '32px', fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ink-2)' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 },
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginTop: 4 },
  emptyRow: { padding: '48px 16px', textAlign: 'center', color: 'var(--muted-2)', fontSize: 14 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: 'var(--surface)', borderRadius: 16, padding: 32, width: 560, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: 18, fontWeight: 700, marginBottom: 20 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 },
  row: { display: 'flex', gap: 8 },
  sectionGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 8 },
  checkItem: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 6, background: 'var(--surface-2)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: 13 },
  divider: { borderTop: '1px solid var(--surface-2)', margin: '20px 0' },
  userRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
};

export default function GroupManagement() {
  const { isSuperAdmin, isAdmin, user } = useAuth();
  const navigate = useNavigate();

  // Only sections the org is entitled to can be granted to groups.
  const availableSections = ALL_SECTIONS.filter(
    s => isSuperAdmin || (user?.org_sections || []).includes(s)
  );

  const [groups, setGroups]       = useState([]);
  const [users, setUsers]         = useState([]);
  const [clusters, setClusters]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  // Create modal
  const [showCreate, setShowCreate]   = useState(false);
  const [createForm, setCreateForm]   = useState({ name: '', description: '' });
  const [createError, setCreateError] = useState('');
  const [creating, setCreating]       = useState(false);

  // Edit modal
  const [editGroup, setEditGroup]     = useState(null);
  const [editForm, setEditForm]       = useState({ name: '', description: '' });
  const [editSections, setEditSections] = useState([]);
  const [editMembers, setEditMembers]   = useState([]);
  const [editClusters, setEditClusters] = useState([]);
  const [editError, setEditError]     = useState('');
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin && !isAdmin) { navigate('/'); return; }
    load();
  }, [isSuperAdmin, isAdmin]);  // eslint-disable-line

  async function load() {
    setLoading(true);
    try {
      // Groups + users are role-gated (no section needed) and are the critical data.
      const [gRes, uRes] = await Promise.all([
        groupsAPI.list(),
        usersAPI.list(),
      ]);
      setGroups(gRes.data);
      setUsers(uRes.data);
      setError('');
      // Clusters only feed the cluster-access picker; tolerate 403 when the org
      // isn't entitled to the clusters section — group management must still work.
      const cRes = await clustersAPI.list().catch(() => ({ data: [] }));
      setClusters(cRes.data || []);
    } catch (e) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true); setCreateError('');
    try {
      const { data: created } = await groupsAPI.create(createForm);
      setShowCreate(false);
      setCreateForm({ name: '', description: '' });
      load();
      // Members, permissions and cluster access are all set in the Edit dialog, which a
      // new group otherwise gave no hint of — open it straight away on the new group.
      if (created?.id) openEdit(created);
    } catch (e) {
      setCreateError(e.response?.data?.detail || 'Failed to create group');
    } finally {
      setCreating(false);
    }
  }

  function openEdit(group) {
    setEditGroup(group);
    setEditForm({ name: group.name, description: group.description || '' });
    setEditSections(group.sections || []);
    setEditMembers(group.member_ids || []);
    setEditClusters(group.cluster_access_ids || []);
    setEditError('');
  }

  async function handleSaveEdit(e) {
    e.preventDefault();
    setEditLoading(true); setEditError('');
    try {
      await Promise.all([
        groupsAPI.update(editGroup.id, { name: editForm.name, description: editForm.description }),
        groupsAPI.assignPermissions(editGroup.id, editSections),
        groupsAPI.assignMembers(editGroup.id, editMembers),
        groupsAPI.assignClusters(editGroup.id, editClusters),
      ]);
      setEditGroup(null);
      load();
    } catch (e) {
      setEditError(e.response?.data?.detail || 'Failed to save changes');
    } finally {
      setEditLoading(false);
    }
  }

  async function handleDelete(group) {
    if (!window.confirm(`Delete group "${group.name}"? This will remove all member assignments.`)) return;
    try {
      await groupsAPI.delete(group.id);
      load();
    } catch (e) {
      setError('Failed to delete group');
    }
  }

  function toggleSection(s) {
    setEditSections(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  }

  function toggleMember(uid) {
    setEditMembers(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  }

  function toggleCluster(cid) {
    setEditClusters(prev => prev.includes(cid) ? prev.filter(x => x !== cid) : [...prev, cid]);
  }

  const getUserName = (uid) => {
    const u = users.find(u => u.id === uid);
    return u ? (u.full_name || u.username) : `User #${uid}`;
  };

  const getClusterName = (cid) => {
    const c = clusters.find(c => c.id === cid);
    return c ? c.name : `Cluster #${cid}`;
  };

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Group Management</h1>
          <div style={S.subtitle}>Create groups and assign permissions &amp; cluster access to multiple users at once</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + New Group
        </button>
      </div>

      {error && <div className="banner danger" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="table-card">
        {loading ? (
          <div style={S.emptyRow}>Loading groups…</div>
        ) : groups.length === 0 ? (
          <div style={S.emptyRow}>
            No groups yet. Create one to manage permissions for multiple users at once.
          </div>
        ) : (
          <table className="ds">
            <thead>
              <tr>
                <th>Group</th>
                <th>Members</th>
                <th>Sections</th>
                <th>Cluster Access</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <tr key={g.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{g.name}</div>
                    {g.description && <div style={{ fontSize: 12, color: 'var(--muted-2)', marginTop: 2 }}>{g.description}</div>}
                  </td>
                  <td>
                    {g.member_ids.length === 0
                      ? <span style={{ color: 'var(--muted-2)', fontSize: 12 }}>No members</span>
                      : g.member_ids.map(uid => (
                          <span key={uid} className="badge brand" style={{ margin: '2px' }}>
                            👤 {getUserName(uid)}
                          </span>
                        ))
                    }
                  </td>
                  <td>
                    {g.sections.length === 0
                      ? <span style={{ color: 'var(--muted-2)', fontSize: 12 }}>None</span>
                      : g.sections.map(s => (
                          <span key={s} className="badge brand" style={{ margin: '2px' }}>
                            {SECTION_LABELS[s] || s}
                          </span>
                        ))
                    }
                  </td>
                  <td>
                    {g.cluster_access_ids.length === 0
                      ? <span style={{ color: 'var(--muted-2)', fontSize: 12 }}>None</span>
                      : g.cluster_access_ids.map(cid => (
                          <span key={cid} className="badge success" style={{ margin: '2px' }}>
                            ☸️ {getClusterName(cid)}
                          </span>
                        ))
                    }
                  </td>
                  <td>
                    <ActionsMenu>
                      <button style={MENU_ITEM} onClick={() => openEdit(g)}>
                        Edit
                      </button>
                      <button style={{ ...MENU_ITEM, color: 'var(--color-danger-500)' }} onClick={() => handleDelete(g)}>
                        Delete
                      </button>
                    </ActionsMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div style={S.overlay} onClick={() => setShowCreate(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <h2 style={S.modalTitle}>Create Group</h2>
            {createError && <div className="banner danger" style={{ marginBottom: 16 }}>{createError}</div>}
            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: 16 }}>
                <label style={S.label}>Group Name *</label>
                <input
                  className="input" required
                  value={createForm.name}
                  onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. DevOps Team"
                />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={S.label}>Description</label>
                <textarea
                  className="textarea"
                  value={createForm.description}
                  onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Optional description"
                />
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted-2)', marginTop: -12, marginBottom: 20 }}>
                Next you'll pick the group's members, permissions and clusters.
              </div>
              <div style={{ ...S.row, justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Creating…' : 'Create & add members'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editGroup && (
        <div style={S.overlay} onClick={() => setEditGroup(null)}>
          <div style={{ ...S.modal, width: 640 }} onClick={e => e.stopPropagation()}>
            <h2 style={S.modalTitle}>Edit Group</h2>
            {editError && <div className="banner danger" style={{ marginBottom: 16 }}>{editError}</div>}
            <form onSubmit={handleSaveEdit}>

              {/* Name & Description */}
              <div style={{ marginBottom: 14 }}>
                <label style={S.label}>Group Name *</label>
                <input
                  className="input" required
                  value={editForm.name}
                  onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={S.label}>Description</label>
                <textarea
                  className="textarea"
                  value={editForm.description}
                  onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                />
              </div>

              <div style={S.divider} />

              {/* Members */}
              <div style={{ marginBottom: 16 }}>
                <label style={S.label}>Members ({editMembers.length} selected)</label>
                <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 8 }}>
                  {users.length === 0
                    ? <div style={{ color: 'var(--muted-2)', fontSize: 13, padding: 8 }}>No users available</div>
                    : users.map(u => {
                        const selected = editMembers.includes(u.id);
                        return (
                          <div
                            key={u.id}
                            style={{ ...S.userRow, background: selected ? 'var(--brand-soft)' : 'transparent' }}
                            onClick={() => toggleMember(u.id)}
                          >
                            <input type="checkbox" checked={selected} readOnly style={{ accentColor: 'var(--brand)' }} />
                            <span style={{ fontWeight: 500 }}>{u.full_name || u.username}</span>
                            <span style={{ color: 'var(--muted-2)', fontSize: 12 }}>{u.email}</span>
                            <span className="badge" style={{ marginLeft: 'auto' }}>
                              {u.rbac_role}
                            </span>
                          </div>
                        );
                      })
                  }
                </div>
              </div>

              <div style={S.divider} />

              {/* Section permissions */}
              <div style={{ marginBottom: 16 }}>
                <label style={S.label}>Section Permissions ({editSections.length} selected)</label>
                <div style={S.sectionGrid}>
                  {availableSections.map(s => {
                    const checked = editSections.includes(s);
                    return (
                      <div key={s} style={{ ...S.checkItem, background: checked ? 'var(--brand-soft)' : 'var(--surface-2)', borderColor: checked ? 'var(--color-brand-300)' : 'var(--border)' }} onClick={() => toggleSection(s)}>
                        <input type="checkbox" checked={checked} readOnly style={{ accentColor: 'var(--brand)' }} />
                        <span>{SECTION_LABELS[s]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={S.divider} />

              {/* Cluster access */}
              <div style={{ marginBottom: 20 }}>
                <label style={S.label}>Cluster Access ({editClusters.length} selected)</label>
                {clusters.length === 0
                  ? <div style={{ color: 'var(--muted-2)', fontSize: 13 }}>No clusters available</div>
                  : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                      {clusters.map(c => {
                        const checked = editClusters.includes(c.id);
                        return (
                          <div
                            key={c.id}
                            onClick={() => toggleCluster(c.id)}
                            style={{ ...S.checkItem, background: checked ? 'var(--color-success-50)' : 'var(--surface-2)', borderColor: checked ? 'var(--color-success-500)' : 'var(--border)', cursor: 'pointer' }}
                          >
                            <input type="checkbox" checked={checked} readOnly style={{ accentColor: 'var(--color-success-500)' }} />
                            <span>☸️ {c.name}</span>
                            <span style={{ fontSize: 11, color: 'var(--muted-2)' }}>({c.environment})</span>
                          </div>
                        );
                      })}
                    </div>
                }
              </div>

              <div style={{ ...S.row, justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setEditGroup(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={editLoading}>
                  {editLoading ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
